import { getTokenBalanceByWallet } from "@/utils/alchemy.config";
import { ethers } from "ethers";
import DecimalNumber from "decimal.js";
import { AccountAddress, ConcordiumGRPCWebClient } from "@concordium/web-sdk";
import { CCD_RPC_URL, CCD_CURRENCY_SYMBOL, CCD_CURRENCY_DECIMALS } from "@/utils/concordium/ccdConstants";
import * as CcdAmountModule from "@concordium/web-sdk/types/CcdAmount";

export const getBalanceFromAlchemy = async (
  address,
  chainId,
  currencies,
  mainBalance
) => {
  const response = await getTokenBalanceByWallet(address, chainId);
  const balances = response?.data?.data?.tokens || [];
  const tokens = balances.map((meta) => {
    if (!meta?.tokenAddress) {
      // its a main token
      return mainBalance;
    }

    // Try to match with AppKit currency data for USD conversion
    const currency = currencies.find(
      (c) => c.id.toUpperCase() === meta.tokenMetadata?.symbol.toUpperCase()
    );
    if (!currency) {
      return null;
    }
    const tokenBalance = ethers.formatUnits(
      meta?.tokenBalance,
      Number(meta?.tokenMetadata?.decimals)
    );
    return {
      symbol: currency.id,
      contractAddress: meta?.tokenAddress,
      tokenBalance,
      logo: currency.logo,
      decimals: currency.decimals,
      currentRate: currency?.currentRate || 0,
      isStableCoin: currency?.isStableCoin || false,
      usdBalance: new DecimalNumber(tokenBalance)
        .mul(currency?.currentRate || 0)
        .toFixed(2),
      chainNetwork: currency["chains.network"] || currency.chainNetwork,
    };
  });
  // 5. Sort and set
  const t = tokens
    .filter((t) => t?.tokenBalance && Number(t.tokenBalance) > 0)
    .sort((a, b) => Number(b.usdBalance) - Number(a.usdBalance));
  return t;
};

export const getBalanceFromConcordium = async (
  address,
  chainId,
  currencies,
  mainBalance
) => {
  console.log("🚀 ~ getBalanceFromConcordium ~ mainBalance:", mainBalance)
  // setup grpc client
  try {
    const rpcUrl = CCD_RPC_URL;
    const url = new URL(rpcUrl);
    const host = url.hostname;
    const port = Number(url.port) || 443;
    const grpcClient = new ConcordiumGRPCWebClient(
      `https://${host}`,
      port
    );
  // get account info
    const accountInfo = await grpcClient.getAccountInfo(AccountAddress.fromBase58(address));
    console.log("🚀 ~ accountInfo:", accountInfo)
    const accountAmount = accountInfo.accountAmount;
    const ccdBalance = CcdAmountModule.toCcd(accountAmount);
    const accountTokens = accountInfo.accountTokens;
    const tokens = accountTokens.map((token) => {
      const value = token.state.balance.value;
      const decimals = token.state.balance.decimals;
      const divisor = BigInt(10) ** BigInt(decimals);

      const integer = value / divisor;
      const fraction = value % divisor;

      const readable =
        decimals === 0
          ? integer.toString()
          : `${integer}.${fraction.toString().padStart(decimals, "0")}`;

      return {
        symbol: token.id.value,
        balance: readable,
        decimals,
      };
    });
    const allTokens = [...tokens, {
      symbol: CCD_CURRENCY_SYMBOL,
      balance: ccdBalance.toFixed(CCD_CURRENCY_DECIMALS),
      decimals: CCD_CURRENCY_DECIMALS,
    }];
    // map currencies to allTokens as above alchemy function
    const mappedTokens = allTokens.map((token) => {
      const currency = currencies.find(
        (c) => c.id.toUpperCase() === token.symbol.toUpperCase()
      );
      if (!currency) {
        return null;
      }
      return {
        symbol: currency.id,
        contractAddress: token.symbol,
        tokenBalance: token.balance,
        logo: currency.logo,
        decimals: CCD_CURRENCY_DECIMALS,
        currentRate: currency?.currentRate || 0,
        isStableCoin: false,
        usdBalance: new DecimalNumber(token.balance)
          .mul(currency?.currentRate || 0)
          .toFixed(2),
        chainNetwork: currency["chains.network"] || currency.chainNetwork,
      };
    });
    // filter null
    const filteredTokens = mappedTokens.filter((t) => t !== null);
    console.log("🚀 ~ getBalanceFromConcordium ~ filteredTokens:", filteredTokens)
    return filteredTokens;
  } catch (error) {
    console.log("🚀 ~ error:", error)
    return [];
  }
};
